import api from './client';
import type { AxiosResponse } from 'axios';

// Settings
export const getSettings = (): Promise<AxiosResponse> => api.get('/settings');
export const updateSettings = (data: Record<string, unknown>): Promise<AxiosResponse> => api.put('/settings', data);
export const changePassword = (data: { current_password: string; new_password: string }): Promise<AxiosResponse> => api.put('/account/password', data);
export const deleteAccount = (currentPassword: string): Promise<AxiosResponse> => api.delete('/account', { data: { current_password: currentPassword } });
export const snoozeAccount = (hours: number): Promise<AxiosResponse> => api.post(`/account/snooze?duration_hours=${hours}`);
export const unsnoozeAccount = (): Promise<AxiosResponse> => api.delete('/account/snooze');
export const exportUserData = (): Promise<AxiosResponse> => api.get('/account/export');
export const updateEmailPreferences = (prefs: Record<string, boolean>): Promise<AxiosResponse> => api.put('/email-preferences', prefs);

// Devices & Sessions
export type SessionDevice = {
  id: string;
  ip: string;
  label: string;
  user_agent: string;
  last_seen: string;
  step_up_passed_at: string | null;
  is_current: boolean;
};
export const listDevices = (): Promise<AxiosResponse<{ devices: SessionDevice[]; total: number }>> => api.get('/account/devices');
export const revokeDevice = (deviceId: string): Promise<AxiosResponse> => api.delete(`/account/devices/${encodeURIComponent(deviceId)}`);
export const signOutOthers = (): Promise<AxiosResponse<{ revoked_count: number; token: string; message: string }>> => api.post('/account/devices/sign-out-others');

// Payments
export const createCheckout = (planId: string, ref?: string): Promise<AxiosResponse> =>
  api.post('/payments/checkout', { plan: planId, origin_url: window.location.origin, ...(ref ? { ref } : {}) });
export const getPaymentStatus = (sessionId: string): Promise<AxiosResponse> => api.get(`/payments/status/${sessionId}`);

// Subscription Management
export const getSubscription = (): Promise<AxiosResponse> => api.get('/subscription');
export const getFoundingStatus = (): Promise<AxiosResponse> => api.get('/founding-status');
export const changeSubscription = (newTier = 'premium', planType = 'monthly'): Promise<AxiosResponse> => api.post('/subscription/change', { new_tier: newTier, plan_type: planType });
export const cancelSubscription = (): Promise<AxiosResponse> => api.post('/subscription/cancel');
export const downgradeSubscription = (): Promise<AxiosResponse> => api.post('/subscription/downgrade');
export const checkPremiumExpiration = (): Promise<AxiosResponse> => api.get('/subscription/expiration-check');

// Safety
export const setSafetyContact = (name: string, phone: string): Promise<AxiosResponse> => api.post('/safety/check-in', { contact_name: name, contact_phone: phone });
export const shareDateWithContact = (datePlanId: string): Promise<AxiosResponse> => api.post('/safety/share-date', { date_plan_id: datePlanId });
export const getSafetyContact = (): Promise<AxiosResponse> => api.get('/safety/contact');
export const getSafetyResources = (category?: string): Promise<AxiosResponse> => api.get(`/safety/resources${category ? `?category=${category}` : ''}`);

// Security
export const setup2FA = (): Promise<AxiosResponse> => api.post('/security/2fa/setup');
export const verify2FA = (code: string): Promise<AxiosResponse> => api.post(`/security/2fa/verify?code=${code}`);
export const disable2FA = (code: string): Promise<AxiosResponse> => api.post(`/security/2fa/disable?code=${code}`);
export const get2FAStatus = (): Promise<AxiosResponse> => api.get('/security/2fa/status');
export const requestBackgroundCheck = (): Promise<AxiosResponse> => api.post('/security/background-check');
export const getBackgroundCheck = (): Promise<AxiosResponse> => api.get('/security/background-check');

// Fair Partner Score insights
export type FairScoreStats = {
  score: number;
  badge: boolean;
  total_dates: number;
  positive_feedbacks: number;
  paid_own_pct: number;
  avg_fairness_rating: number;
  avg_budget_adherence: number;
  trend_vs_last_month: number;
  prior_score: number;
  tips: { title: string; body: string }[];
};
export const getFairScoreStats = (): Promise<AxiosResponse<FairScoreStats>> => api.get('/account/fair-score-stats');

// Legal (Terms + Privacy + EULA) — admin-editable
export type LegalDocType = 'terms' | 'privacy' | 'eula' | 'acceptableuse' | 'lawenforcement' | 'refunds' | 'pricingdisclosures' | 'support';
export type LegalDoc = {
  doc_type: LegalDocType;
  version: number;
  content_html: string;
  updated_at: string | null;
  changelog: string;
  finalized?: boolean;
};
export type LegalStatus = {
  docs: Array<{
    doc_type: 'terms' | 'privacy';
    current_version: number;
    accepted_version: number;
    needs_accept: boolean;
    material_change: boolean;
    changelog: string;
  }>;
  needs_accept_any: boolean;
  hard_block: boolean;
};
export const getLegalDoc = (docType: LegalDocType): Promise<AxiosResponse<LegalDoc>> =>
  api.get(`/legal/${docType}`);
export const publishLegalDoc = (
  docType: LegalDocType,
  body: { content_html: string; material_change: boolean; changelog: string },
): Promise<AxiosResponse<{ doc_type: string; version: number; notified_users: number; material_change: boolean }>> =>
  api.put(`/admin/legal/${docType}`, body);
export type LegalVersionSummary = {
  id: string;
  version: number;
  material_change: boolean;
  changelog: string;
  published_at: string | null;
  published_by: string;
  archived_at: string;
  archived_by_publish: string;
};
export const listLegalVersions = (docType: LegalDocType): Promise<AxiosResponse<{ doc_type: string; versions: LegalVersionSummary[] }>> =>
  api.get(`/admin/legal/${docType}/versions`);
export const getLegalVersion = (docType: LegalDocType, versionId: string): Promise<AxiosResponse<LegalVersionSummary & { content_html: string }>> =>
  api.get(`/admin/legal/${docType}/versions/${versionId}`);
export const rollbackLegalDoc = (docType: LegalDocType, versionId: string, changelog: string): Promise<AxiosResponse<{ doc_type: string; version: number }>> =>
  api.post(`/admin/legal/${docType}/rollback`, { version_id: versionId, changelog });
export const getMyLegalStatus = (): Promise<AxiosResponse<LegalStatus>> => api.get('/legal/status/me');
export const acceptLegal = (docType: 'terms' | 'privacy' | 'both'): Promise<AxiosResponse> =>
  api.post('/legal/accept', { doc_type: docType });

// Help & Legal centre — directory + cross-document search (public)
export type LegalDirectoryDoc = { doc_type: LegalDocType; title: string; path: string; desc: string };
export const getLegalDirectory = (): Promise<AxiosResponse<{ docs: LegalDirectoryDoc[] }>> =>
  api.get('/legal/directory');
export type LegalSearchResult = {
  doc_type: LegalDocType; doc_title: string; path: string; heading: string; snippet: string; score: number;
};
export const searchLegal = (q: string): Promise<AxiosResponse<{ query: string; results: LegalSearchResult[] }>> =>
  api.get(`/legal/search`, { params: { q } });
// Admin one-tap — finalize Terms + Privacy (material re-consent for all users)
export const finalizeLegal = (changelog?: string): Promise<AxiosResponse<{ finalized: boolean; docs: Array<{ doc_type: string; version: number }>; notified_users: number }>> =>
  api.post('/admin/legal/finalize', changelog ? { changelog } : {});

// Block & Report
export const blockUser = (userId: string): Promise<AxiosResponse> => api.post(`/users/${userId}/block`);
export const unblockUser = (userId: string): Promise<AxiosResponse> => api.post(`/users/${userId}/unblock`);
export const getBlockedUsers = (): Promise<AxiosResponse> => api.get('/blocks');
export const reportUser = (userId: string, reason: string, details = '', messageId?: string): Promise<AxiosResponse> => api.post(`/users/${userId}/report`, { reason, details, message_id: messageId });

// Travel & Incognito
export const setTravelMode = (location: string): Promise<AxiosResponse> => api.post('/travel/set', { location });
export const clearTravelMode = (): Promise<AxiosResponse> => api.post('/travel/clear');
export const toggleIncognito = (): Promise<AxiosResponse> => api.post('/incognito/toggle');
export const boostProfile = (): Promise<AxiosResponse> => api.post('/boost');

// Calendar
export const getCalendarProviders = (): Promise<AxiosResponse> => api.get('/calendar/providers');
export const connectCalendar = (provider: string): Promise<AxiosResponse> => api.post(`/calendar/connect?provider=${provider}`);
export const disconnectCalendar = (provider: string): Promise<AxiosResponse> => api.post(`/calendar/disconnect?provider=${provider}`);
export const getMyAvailability = (daysAhead = 7): Promise<AxiosResponse> => api.get(`/calendar/availability?days_ahead=${daysAhead}`);
export const getMatchAvailability = (userId: string, daysAhead = 7): Promise<AxiosResponse> => api.get(`/calendar/availability/${userId}?days_ahead=${daysAhead}`);
export const shareAvailability = (matchId: string): Promise<AxiosResponse> => api.post(`/calendar/share?match_id=${matchId}`);
export const unshareAvailability = (matchId: string): Promise<AxiosResponse> => api.post(`/calendar/unshare?match_id=${matchId}`);
